import React from "react"
import {
  Bar,
  BarChart,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis
} from "recharts"

import { globalMapStatusStyle } from "../helpers/status-style"
import type { AnalyticsRecordsByStatusRow } from "../redux/global-map.types"
import { ANALYTICS_STATUS_OTHER_COLOR } from "./analytics-constants"

interface RecordsByStatusBarProps {
  rows: AnalyticsRecordsByStatusRow[]
  onSelectStatus: (row: AnalyticsRecordsByStatusRow) => void
}

/**
 * Spec §5: top 5 statuses + "Other" (already rolled up server-side).
 * Colored per `globalMapStatusStyle` — the SAME canonical status color map
 * `ResultsList`/`ListTab`/the map markers already use — "Other" gets a
 * neutral slate rather than a fabricated status color, since it isn't one
 * real status. Clicking a bar applies that status filter (spec) — "Other"
 * is deliberately not clickable (it maps to no single filterable value).
 *
 * Uses `.color` (the pill's own bold TEXT color), not `.backgroundColor` —
 * live-verified: that pale background, correct for a small text badge, read
 * as a near-invisible, washed-out bar at this size. The bold color is still
 * the same canonical per-status palette, just its other, already-designed
 * high-contrast half.
 */
export function RecordsByStatusBar({
  rows,
  onSelectStatus
}: RecordsByStatusBarProps) {
  const chartData = rows.map((row) => ({
    label: row.label,
    count: row.count,
    entity: row.entity,
    color:
      row.entity && row.label !== "Other"
        ? globalMapStatusStyle(row.entity, row.label).color
        : ANALYTICS_STATUS_OTHER_COLOR
  }))

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 8, left: 8, bottom: 0 }}
      >
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: "#64748b" }}
          interval={0}
        />
        <RechartsTooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
        <Bar
          dataKey="count"
          radius={[4, 4, 0, 0]}
          label={{ position: "top", fontSize: 12 }}
        >
          {chartData.map((entry) => (
            <Cell
              key={entry.label}
              fill={entry.color as string}
              cursor={entry.entity ? "pointer" : "default"}
              onClick={() => {
                const row = rows.find((r) => r.label === entry.label)
                if (row?.entity) onSelectStatus(row)
              }}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
